import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  IconButton,
  Divider,
  Tooltip,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import {
  assignFolders,
  getAllFoldersForAdmin,
  getUserFoldersAndFiles,
  removePermissions,
  validatePath,
  getAllUsersAndFoldersPermissions 
} from "../api";
import { toast } from "react-toastify";
import {
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
} from "@mui/icons-material"; // Nova importação
import LaptopMacTwoToneIcon from "@mui/icons-material/LaptopMacTwoTone";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"; // Importação do ícone de lixeira
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import CloseIcon from "@mui/icons-material/Close";

// Definindo a interface para o nó de pasta
interface FolderNode {
  path: string;
  isExpanded: boolean;
  children: FolderNode[];
  hasChildren: boolean; // Adicionado para verificar subpastas
}

// Definindo a interface para as propriedades do modal
interface EditPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  user: any;
  loggedInUsername: string;
}

// Componente funcional EditPermissionsModal
const EditPermissionsModal: React.FC<EditPermissionsModalProps> = ({
  open,
  onClose,
  user,
  loggedInUsername,
}) => {
  // Estado para armazenar as permissões e a árvore de pastas
  const [permissions, setPermissions] = useState<
    { directory: string; writable: boolean }[]
  >([]);
  const [folderTree, setFolderTree] = useState<FolderNode[]>([]);
  const [selectedPath, setSelectedPath] = useState<string>("");
  const [toRemovePermissions, setToRemovePermissions] = useState<string[]>([]);
  const folderRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isButtonActive, setIsButtonActive] = useState(false); // Para controlar o botão "Permitir"
  const [hovered, setHovered] = useState(false); // Para detectar quando o mouse passa por cima do botão
  const [originalPermissions, setOriginalPermissions] = useState<
    { directory: string; writable: boolean }[]
  >([]);
  const [newPermissions, setNewPermissions] = useState<string[]>([]);
  const [requestCount, setRequestCount] = useState(0);
  const [subfoldersMap, setSubfoldersMap] = useState<{
    [parentPath: string]: string[];
  }>({});
  const [isSaving, setIsSaving] = useState(false);

  // Efeito que carrega as pastas quando o modal é aberto
  useEffect(() => {
    if (open) {
      // Limpa as permissões e os estados do modal ao abrir
      setPermissions([]);
      setOriginalPermissions([]);
      setSelectedPath("");
      setFolderTree([]);

      // Busca as pastas para popular o modal
      fetchFolders(loggedInUsername, "", (folders) => {
        setFolderTree(
          folders.map((folder) => ({
            path: folder.path,
            isExpanded: false,
            children: [],
            hasChildren: folder.hasChildren,
          }))
        );
      });

      // Carregar a estrutura de pastas e armazenar no sessionStorage
      fetchAndStoreFolders(loggedInUsername).then((folders) => {});
    }
  }, [open, loggedInUsername]);

  // Função que busca as pastas no backend
  const fetchFolders = async (
    loggedInUsername: string,
    path: string,
    callback: (folders: FolderNode[]) => void
  ) => {
    setLoading(true); // Ativa o estado de carregamento
    try {
      const result = await getAllFoldersForAdmin(loggedInUsername, path);
      const folderNodes = result.map((item) => ({
        path: item.path,
        isExpanded: false,
        children: [],
        hasChildren: !!item.hasChildren, // Converte para booleano
      }));
      callback(folderNodes); // Atualiza a árvore de pastas
    } catch (err: any) {
      // Exibe o erro no console e notifica o usuário
      const errorMessage =
        err.response?.data?.message || "Erro ao carregar as pastas.";
      console.error("Erro ao acessar a pasta:", errorMessage);

      // Exibe um toast com o erro
      toast.error(
        `Erro ao carregar a pasta. Verifique as permissões ou tente novamente.`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    } finally {
      setLoading(false); // Desativa o estado de carregamento
    }
  };

  const fetchAndStoreFolders = async (username: string) => {
    try {
      const folders = await getUserFoldersAndFiles(username);
      sessionStorage.setItem("folderTree", JSON.stringify(folders)); // Armazena no sessionStorage
      return folders;
    } catch (error) {
      console.error("Erro ao buscar pastas:", error);
      return [];
    }
  };

  const getSubfolders = (parentPath: string): string[] => {
    const folderTree = JSON.parse(sessionStorage.getItem("folderTree") || "[]");
    return folderTree
      .filter((folder: any) => folder.path.startsWith(parentPath))
      .map((folder: any) => folder.path);
  };

  const handleRemovePermission = (index: number) => {
    const permission = permissions[index];
    const isOriginal = originalPermissions.some(
      (orig) => orig.directory === permission.directory
    );

    // Se for uma permissão nova (fundo verde), marque para remoção
    if (isOriginal) {
      // Marcar a pasta para remoção (fundo verde)
      const subpathsToRemove = permissions
        .filter((perm) => perm.directory.startsWith(permission.directory))
        .map((perm) => perm.directory);

      setToRemovePermissions((prev) => [
        ...prev,
        ...subpathsToRemove.filter((path) => !prev.includes(path)),
      ]);

      // Atualiza as permissões para mostrar o fundo verde
      setPermissions((prevPermissions) => {
        return prevPermissions.map((perm) => {
          if (subpathsToRemove.includes(perm.directory)) {
            return {
              ...perm,
              writable: false, // Mantém o estado de escrita original
              backgroundColor: "#e8f5e9", // Fundo verde para as pré-removidas
            };
          }
          return perm;
        });
      });
    } else {
      // Caso contrário, apenas remove a pasta
      const updatedPermissions = permissions.filter(
        (perm) => !perm.directory.startsWith(permission.directory)
      );
      setPermissions(updatedPermissions);
    }
  };

  const handleCancelRemovePermission = (index: number) => {
    const permission = permissions[index];

    // Verifica se o item está marcado para exclusão
    if (toRemovePermissions.includes(permission.directory)) {
      // Remove da lista de exclusão
      setToRemovePermissions((prev) =>
        prev.filter((path) => !path.startsWith(permission.directory))
      );

      // Restaura o estado das permissões e fundo para as pastas afetadas
      setPermissions((prev) => {
        return prev.map((perm) => {
          if (perm.directory.startsWith(permission.directory)) {
            const originalPerm = originalPermissions.find(
              (orig) => orig.directory === perm.directory
            );
            return originalPerm
              ? {
                  ...perm,
                  backgroundColor: "transparent", // Remove o fundo verde
                  writable: originalPerm.writable, // Restaura a permissão original
                }
              : perm;
          }
          return perm;
        });
      });
    }
  };

  const handleSavePermissions = async () => {
    setIsSaving(true);

    try {
      // Determinar permissões a serem adicionadas ou alteradas
      const permissionsToAdd = permissions.filter((perm) => {
        const original = originalPermissions.find(
          (orig) => orig.directory === perm.directory
        );
        return (
          !original || // Diretório não está na lista original
          original.writable !== perm.writable // Ou o atributo `writable` foi alterado
        );
      });

      // Expandir permissões com subpastas armazenadas no sessionStorage
      const allPermissionsToAdd: {
        FolderPath: string;
        AccessPermission: boolean;
      }[] = permissionsToAdd.map((perm) => ({
        FolderPath: perm.directory,
        AccessPermission: perm.writable,
      }));      

      // Remover duplicados do array `allPermissionsToAdd`
      const uniquePermissions = Array.from(
        new Map(
          allPermissionsToAdd.map((perm) => [perm.FolderPath, perm])
        ).values()
      );

      // Chamar API de atribuição de permissões
      if (uniquePermissions.length > 0) {
        await assignFolders(loggedInUsername, user?.login, uniquePermissions);
        toast.success("Permissões adicionadas ou alteradas com sucesso!", {
          position: "top-right",
          autoClose: 5000,
        });
      }

      // Remover permissões no backend
      const permissionsToRemove = originalPermissions.filter((perm) =>
        toRemovePermissions.includes(perm.directory)
      );

      if (permissionsToRemove.length > 0) {
        await removePermissions(
          loggedInUsername,
          user?.login,
          permissionsToRemove.map((perm) => perm.directory)
        );
        toast.success("Permissões removidas com sucesso!", {
          position: "top-right",
          autoClose: 5000,
        });
      }

      // Atualizar estado local
      setOriginalPermissions(
        [...originalPermissions, ...permissionsToAdd].filter(
          (perm) => !toRemovePermissions.includes(perm.directory)
        )
      );
      setToRemovePermissions([]);
      setPermissions([]);
      onClose(); // Fecha o modal
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido";
      toast.error(`Erro ao salvar permissões: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    // Verifica se o caminho selecionado é válido (não sendo "Área de trabalho" ou vazio)
    if (selectedPath !== "") {
      setIsButtonActive(true);
    } else {
      setIsButtonActive(false);
    }
  }, [selectedPath]);

  const handleButtonClick = async () => {
    setLoading(true); // Ativar o estado de carregamento
  
    try {
      // Verificar se a pasta já está na lista de permissões
      const isAlreadyAdded = permissions.some((perm) => perm.directory === selectedPath);
  
      if (isAlreadyAdded) {
        toast.info("Esta pasta já está na lista de permissões.", {
          position: "top-right",
          autoClose: 5000,
        });
        setLoading(false);
        return;
      }
  
      // Valida o caminho selecionado no backend
      const isValid = await validatePath(loggedInUsername, selectedPath);
  
      if (!isValid) {
        toast.warning("O caminho selecionado é restrito e não pode ser adicionado.", {
          position: "top-right",
          autoClose: 5000,
        });
        setLoading(false);
        return;
      }
  
      // Adiciona somente a pasta selecionada (sem subpastas)
      setPermissions((prev) => [
        ...prev,
        { directory: selectedPath, writable: false },
      ]);
  
      toast.success("Pasta atribuída. Clique em Salvar para concluir.", {
        position: "top-right",
        autoClose: 5000,
      });
    } catch (error) {
      toast.error(
        `Erro ao adicionar permissões: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    // Limpar estados ao fechar o modal
    setPermissions([]); // Limpa as permissões
    setToRemovePermissions([]); // Limpa os itens marcados para remoção
    setSelectedPath(""); // Reseta o caminho selecionado
    onClose(); // Chama a função passada por props para fechar o modal
  };

  // Função que alterna a expansão ou colapso de uma pasta
  const handleToggleFolder = (node: FolderNode) => {
    setSelectedPath(node.path); // Atualiza o caminho selecionado

    if (node.isExpanded) {
      node.isExpanded = false;
      node.children = [];
      setFolderTree([...folderTree]); // Atualiza a árvore de pastas
    } else {
      setLoading(true); // Exibe o indicador de carregamento
      fetchFolders(loggedInUsername, node.path, (folders) => {
        node.isExpanded = true;
        node.children = folders.map((folder) => ({
          path: folder.path,
          isExpanded: false,
          children: [],
          hasChildren: folder.hasChildren, // Certifique-se de usar essa propriedade
        }));
        setFolderTree([...folderTree]);
      });
    }
  };

  useEffect(() => {
    if (open && user) {
      // Buscar permissões para o usuário selecionado
      fetchUserPermissions(user?.login);
    }
  }, [open, user]);

  // Função para filtrar as pastas que não têm pai
  const filterRootFolders = (
    folders: {
      path: string;
      isDirectory: boolean;
      accessPermission?: boolean;
    }[]
  ): { directory: string; writable: boolean }[] => {
    // Cria um conjunto de todos os caminhos para rápida verificação
    const folderPaths = new Set(folders.map((folder) => folder.path));

    // Filtra apenas as pastas que não têm um pai na lista
    return folders
      .filter((folder) => {
        const parentPath = folder.path
          .split("\\")
          .slice(0, -1) // Remove a última parte para obter o caminho do pai
          .join("\\");
        return !folderPaths.has(parentPath);
      })
      .map((folder) => ({
        directory: folder.path,
        writable: folder.accessPermission ?? false, // Trata undefined como false
      }));
  };

  // Função que busca as permissões do usuário
  // Função que busca as permissões do usuário e filtra as pastas
  const fetchUserPermissions = async (username: string) => {
    try {
      const allUsers = await getAllUsersAndFoldersPermissions(loggedInUsername);
      const currentUser = allUsers.find((u: any) => u.login === username);
  
      if (!currentUser) {
        toast.warning("Usuário não encontrado nas permissões.");
        return;
      }
  
      // Considera apenas as permissões com caminho válido
      const permissionsList = currentUser.permissions.filter(
        (p: any) => p.folderPath && p.folderPath.trim() !== ""
      );
  
      const filteredPermissions = filterRootFolders(
        permissionsList.map((perm: any) => ({
          path: perm.folderPath,
          isDirectory: true,
          accessPermission: perm.accessPermission ?? false,
        }))
      );
  
      setPermissions(filteredPermissions);
      setOriginalPermissions(filteredPermissions);
    } catch (err) {
      console.error("Erro ao buscar permissões para o usuário:", err);
      toast.error("Erro ao carregar permissões do usuário.");
    }
  };
  

  const handleBreadcrumbClick = (path: string) => {
    const formattedPath = path.endsWith("\\") ? path : `${path}\\`;

    // Verificar se o caminho clicado é a raiz (Área de Trabalho), e desabilitar o botão
    if (formattedPath === "\\") {
      setSelectedPath(""); // Limpa o caminho
      setIsButtonActive(false); // Desabilita o botão
    } else {
      setSelectedPath(formattedPath); // Atualiza o caminho selecionado
      setIsButtonActive(true); // Garante que o botão "Permitir" estará ativo
    }

    // Se o caminho estiver vazio, resetar a árvore de pastas
    if (!formattedPath || formattedPath === "\\") {
      const resetTree = (nodes: FolderNode[]): FolderNode[] =>
        nodes.map((node) => ({
          ...node,
          isExpanded: false,
          children: [],
        }));

      setFolderTree(resetTree(folderTree));
      return;
    }

    const findAndExpandNode = (
      nodes: FolderNode[],
      targetPath: string
    ): FolderNode[] =>
      nodes.map((node) => {
        if (targetPath.startsWith(node.path)) {
          return {
            ...node,
            isExpanded: true,
            children: findAndExpandNode(node.children, targetPath),
          };
        }
        return { ...node, isExpanded: false, children: [] };
      });

    const updatedTree = findAndExpandNode(folderTree, formattedPath);
    setFolderTree(updatedTree);

    setTimeout(() => {
      const targetElement = folderRefs.current[formattedPath];
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        setTimeout(() => {
          const targetElementRetry = folderRefs.current[formattedPath];
          if (targetElementRetry) {
            targetElementRetry.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 300);
      }
    }, 300);
  };

  const renderFolderTree = (
    nodes: FolderNode[],
    parentPath: string = "",
    level: number = 0
  ) => {
    return nodes.map((node) => {
      const folderName = parentPath
        ? node.path.replace(parentPath, "")
        : node.path;
      const displayName = folderName.split("\\").pop() || folderName;

      const isMarkedForRemoval = toRemovePermissions.includes(node.path); // Verifica se o item está marcado para remoção

      return (
        <Box
          key={node.path}
          ref={(el: HTMLDivElement | null) => {
            if (el) {
              folderRefs.current[
                node.path.endsWith("\\") ? node.path : `${node.path}\\`
              ] = el;
            }
          }}
          sx={{
            marginLeft: node.hasChildren
              ? level === 0
                ? 0
                : 2
              : level === 0
                ? 0
                : 2.4,
            marginBottom: -0.8,
            transition: "all 0.3s ease",
            backgroundColor: isMarkedForRemoval
              ? (theme) =>
                  theme.palette.mode === "dark" ? "#57140f" : "#fbe9e7" // Fundo vermelho para exclusão
              : "transparent", // Fundo normal para itens não removidos
          }}
        >
          <Box display="flex" alignItems="center">
            {node.hasChildren ? (
              <IconButton size="small" onClick={() => handleToggleFolder(node)}>
                {node.isExpanded ? (
                  <RemoveCircleIcon sx={{ fontSize: 20 }} />
                ) : (
                  <AddCircleIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 27 }} />
            )}
            <Typography
              sx={{
                cursor: "pointer",
                backgroundColor:
                  node.path === selectedPath
                    ? (theme) =>
                        theme.palette.mode === "dark" ? "#4a90e2" : "#e3f2fd"
                    : "transparent",
                padding: "2px 4px",
                borderRadius: 1,
                transition: "background-color 0.3s ease",
              }}
              onClick={() => setSelectedPath(node.path)} // Atualiza o caminho selecionado
            >
              {displayName}
            </Typography>
          </Box>
          {node.isExpanded && (
            <Box sx={{ marginLeft: 2 }}>
              {renderFolderTree(node.children, node.path + "\\", level + 1)}
            </Box>
          )}
        </Box>
      );
    });
  };

  // Função que renderiza o breadcrumb
  const renderBreadcrumb = () => (
    <>
      <IconButton
        sx={{
          cursor: "pointer",
          color: "#1976d2",
          marginRight: 1,
        }}
        onClick={() => handleBreadcrumbClick("")} // Limpa o caminho e desabilita o botão "Permitir"
      >
        <LaptopMacTwoToneIcon />
      </IconButton>

      {selectedPath &&
        selectedPath
          .split("\\")
          .filter(Boolean)
          .map((part, index, array) => {
            const path = array.slice(0, index + 1).join("\\");
            return (
              <React.Fragment key={path}>
                {/* Omitir a barra invertida antes do primeiro item */}
                {index > 0 && (
                  <Typography
                    sx={{
                      display: "inline",
                      color: "#000",
                    }}
                  >
                    \
                  </Typography>
                )}
                <Typography
                  sx={{
                    cursor: "pointer",
                    color: "#1976d2",
                    textDecoration: "underline",
                    display: "inline",
                  }}
                  onClick={() => handleBreadcrumbClick(path)}
                >
                  {part}
                </Typography>
              </React.Fragment>
            );
          })}
    </>
  );

  // Função para alternar a permissão de escrita
  const handleTogglePermission = (index: number) => {
    const selectedPermission = permissions[index];
    const newWritableState = !selectedPermission.writable;

    // Busca todas as subpastas da pasta selecionada
    const subfolders = getSubfolders(selectedPermission.directory);

    // Atualiza permissões de pai e subpastas
    setPermissions((prevPermissions) => {
      return prevPermissions.map((perm) => {
        if (
          perm.directory === selectedPermission.directory ||
          subfolders.includes(perm.directory)
        ) {
          return { ...perm, writable: newWritableState };
        }
        return perm;
      });
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose} // Usar a função centralizada
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "65%",
          height: "60vh",
          maxHeight: "65vh",
        },
      }}
    >
      <Backdrop
        open={loading || isSaving} // Mostra o indicador durante o carregamento ou salvamento
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogTitle>
        Gerenciar Permissões para: {user?.login} - {user?.fullName}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8, // Alinha o ícone à direita
            top: 8, // Alinha o ícone ao topo
            color: (theme) => theme.palette.grey[500], // Define a cor do ícone
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center", // Centraliza os itens verticalmente
          gap: 2,
          height: "calc(100% - 64px)",
        }}
      >
        {/* Box para as pastas */}
        <Box
          sx={{
            flex: 1,
            border: "1px solid #ccc",
            borderRadius: 1,
            padding: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box>
            <strong>Pastas</strong>
          </Box>

          <Box
            sx={{
              fontSize: "0.9rem",
              color: "#555",
              wordBreak: "break-all",
            }}
          >
            {renderBreadcrumb()}
          </Box>

          <Divider />
          <Box
            sx={{
              marginTop: 2,
              overflowY: "auto",
              flexGrow: 1,
            }}
          >
            {renderFolderTree(folderTree)}
          </Box>
        </Box>

        {/* Botão de Permitir */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center", // Centraliza verticalmente
            alignItems: "center", // Centraliza horizontalmente
            border: "1px solid #ccc",
            padding: "4px 8px",
            borderRadius: "8px",
            cursor: isButtonActive ? "pointer" : "not-allowed",
            backgroundColor: isButtonActive ? "transparent" : "#f0f0f0",
            height: "fit-content",
            textAlign: "center",
          }}
          onClick={isButtonActive ? handleButtonClick : undefined}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <IconButton
            sx={{
              fontSize: 36,
              backgroundColor: "transparent",
              cursor: isButtonActive ? "pointer" : "not-allowed",
              "&:hover": {
                backgroundColor: "transparent",
              },
              padding: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: "bold",
                color:
                  isButtonActive && hovered
                    ? "#4CAF50"
                    : (theme) =>
                        theme.palette.mode === "dark" ? "#B0B0B0" : "#888",
                textAlign: "center", // Centraliza o texto
                lineHeight: 1, // Controla o espaçamento vertical
              }}
            >
              <span style={{ lineHeight: 1.2 }}>
                Atribuir
                <br />
              </span>
              <span style={{ lineHeight: 1.2 }}>pasta</span>
            </Typography>

            <DoubleArrowIcon
              sx={{
                fontSize: 42,
                color:
                  isButtonActive && hovered
                    ? "#4CAF50"
                    : (theme) =>
                        theme.palette.mode === "dark" ? "#B0B0B0" : "#888",
              }}
            />
          </IconButton>
        </Box>

        {/* Box para as permissões */}
        <Box
          sx={{
            flex: 2, // Maior largura relativa para a caixa de Permissões
            border: "1px solid #ccc",
            borderRadius: 1,
            padding: 2,
            height: "100%", // Define a altura fixa para o contêiner
            display: "flex",
            flexDirection: "column",
          }}
        >
          <strong>Permissões de Acesso</strong>
          {permissions.length > 0 ? (
            <Box
              sx={{
                flexGrow: 1, // Faz o contêiner ocupar o espaço restante
                overflowY: "auto", // Permite a rolagem
              }}
            >
              <Table
                size="small"
                stickyHeader // Ativa o cabeçalho fixo
                sx={{
                  borderSpacing: "0px", // Reduz o espaçamento entre as linhas e colunas
                  "& .MuiTableCell-root": {
                    padding: "2px 6px", // Reduz ainda mais o padding dos itens da tabela
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: "0.8em", // Define o tamanho do texto para os cabeçalhos
                        fontWeight: "bold",
                        borderBottom: "1px solid #ccc", // Linha cinza entre cabeçalho e corpo
                      }}
                    >
                      Diretório
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "0.8em", // Define o tamanho do texto para os cabeçalhos
                        fontWeight: "bold",
                        borderBottom: "1px solid #ccc", // Linha cinza entre cabeçalho e corpo
                      }}
                    >
                      Permitir
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "0.8em", // Define o tamanho do texto para os cabeçalhos
                        fontWeight: "bold",
                        borderBottom: "1px solid #ccc", // Linha cinza entre cabeçalho e corpo
                      }}
                    >
                      Excluir
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {permissions
                    .sort((a, b) => {
                      const isNewA = !originalPermissions.some(
                        (orig) => orig.directory === a.directory
                      );
                      const isNewB = !originalPermissions.some(
                        (orig) => orig.directory === b.directory
                      );

                      if (isNewA && !isNewB) return -1;
                      if (!isNewA && isNewB) return 1;

                      return a.directory.localeCompare(b.directory);
                    })
                    .map((perm, index) => {
                      const isNew = !originalPermissions.some(
                        (orig) => orig.directory === perm.directory
                      );

                      // Calcular o nível do diretório (quantidade de barras invertidas)
                      const level = perm.directory.split("\\").length - 1;

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: toRemovePermissions.includes(
                              perm.directory
                            )
                              ? (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "#57140f"
                                    : "#fbe9e7"
                              : isNew
                                ? (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "#0f5714"
                                      : "#e8f5e9"
                                : "transparent",
                            transition: "background-color 0.3s",
                            height: "18px", // Diminuindo a altura de cada linha
                            "& td, & th": {
                              padding: "2px 4px", // Reduzindo o padding interno
                            },
                          }}
                        >
                          {/* Diretório com indentação */}
                          <TableCell
                            sx={{
                              fontSize: "0.75em", // Diminui o tamanho do texto
                              lineHeight: 1.2, // Reduz o espaçamento vertical do texto
                              borderBottom: "1px solid #ccc", // Linha divisória mais discreta
                              paddingLeft: `${level * 8}px`, // Indentação proporcional ao nível
                            }}
                          >
                            {perm.directory}
                          </TableCell>

                          {/* Checkbox para alternar permissão de escrita */}
                          <TableCell
                            align="center"
                            sx={{
                              lineHeight: 1,
                              borderBottom: "1px solid #ccc",
                              width: "20px", // Largura fixa para a coluna de checkbox
                            }}
                          >
                            <Checkbox
                              checked={perm.writable}
                              onChange={() => handleTogglePermission(index)}
                              size="small"
                              sx={{
                                padding: 0,
                                margin: 0,
                              }}
                            />
                          </TableCell>

                          {/* Botão de exclusão/remover */}
                          <TableCell
                            align="center"
                            sx={{
                              borderBottom: "1px solid #ccc",
                              width: "50px", // Largura fixa para a coluna de ações
                            }}
                          >
                            <Tooltip
                              title={
                                toRemovePermissions.includes(perm.directory)
                                  ? "Cancelar exclusão"
                                  : isNew
                                    ? "Remover"
                                    : "Excluir"
                              }
                              placement="right"
                            >
                              {toRemovePermissions.includes(perm.directory) ? (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCancelRemovePermission(index)
                                  }
                                  sx={{
                                    color: "#4CAF50",
                                    fontSize: "16px",
                                  }}
                                >
                                  <RestoreFromTrashIcon />
                                </IconButton>
                              ) : isNew ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemovePermission(index)}
                                  sx={{
                                    color: "#d32f2f",
                                    fontSize: "16px",
                                  }}
                                >
                                  <DoubleArrowIcon
                                    sx={{
                                      transform: "rotate(180deg)",
                                      fontSize: "16px",
                                    }}
                                  />
                                </IconButton>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemovePermission(index)}
                                  sx={{
                                    color: "#888", // Cinza padrão
                                    fontSize: "16px",
                                    "&:hover": {
                                      color: "#d32f2f", // Vermelho ao passar o mouse
                                    },
                                  }}
                                >
                                  <DeleteForeverIcon />
                                </IconButton>
                              )}
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                color: "#999",
                marginTop: 2,
                fontSize: "0.9rem",
              }}
            >
              Nenhuma permissão para este usuário.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column", // Mantém a organização em coluna
          alignItems: "flex-start", // Alinha as legendas à esquerda
          marginTop: -2, // Espaço acima da legenda
          paddingLeft: { xs: 8, sm: 16, md: 32, lg: 67 }, // Ajusta o paddingLeft dinamicamente
          gap: -2, // Espaço entre as legendas
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            color: (theme) => theme.palette.text.primary,
          }}
        >
          Legenda:
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: "#d32f2f",
              border: "1px solid #e8f5e9",
            }}
          />
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: (theme) => theme.palette.text.primary,
            }}
          >
            Pasta que será removida.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: "#4dfa5b",
              border: "1px solid #e8f5e9",
            }}
          />
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: (theme) => theme.palette.text.primary,
            }}
          >
            Pasta que será atribuída.
          </Typography>
        </Box>
      </Box>

      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.primary.main,
            textTransform: "uppercase",
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSavePermissions}
          color="secondary"
          disabled={(() => {
            const isDisabled =
              isSaving ||
              !permissions.some((perm) => {
                const original = originalPermissions.find(
                  (orig) => orig.directory === perm.directory
                );
                return (
                  !original || // Diretório não está na lista original
                  original.writable !== perm.writable || // Campo "writable" foi alterado
                  toRemovePermissions.includes(perm.directory) // Diretório marcado para remoção
                );
              });
            return isDisabled;
          })()}
          sx={{
            backgroundColor: "transparent !important",
            "&.Mui-disabled": {
              color: "#ccc", // Cor cinza para o texto desativado
              backgroundColor: "transparent",
            },
            "&:not(.Mui-disabled)": {
              color: "#1976d2",
            },
          }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Exportação do componente
export default EditPermissionsModal;
